import { callAct, isOk } from './apiConfig';

/*
 * permissions — real staff / roles / capability data for the הרשאות panel.
 *
 * Wires the existing PHP acts (php/act.php):
 *   - groupsList        → the roles / permission GROUPS of the business
 *                         data: [{ GroupName, Permissions }]  (groupID stripped server-side)
 *   - permissionsList   → capability rows × which groups grant them
 *                         data: [{ ID, groupID:"1,2", groupName:"בעלים, מנהל", permissionName }]
 *   - employeeList      → the staff of the business
 *                         data: { employeesList:[{ userGroupID, userID, groupName,
 *                                 privateName, lastName, textType, businessNames }],
 *                                 groupsList:[{ groupID(enc), name, ... }] }
 *   - addEmployee       → creates a pending SMS-invite for a phone + group
 *                         input: { phone, employeeGroup(enc groupID), smsNotification,
 *                                  expiredTime }  →  data: { ID }
 *
 * NOTE — no backend act exists yet for: change an employee's group, disable/enable,
 * remove an employee, or list/cancel pending invites. Those controls are surfaced
 * as disabled with a reason rather than faking success (see Permissions.js).
 *
 * Every function returns a normalized value and fails gracefully (never throws).
 */

// Roles / permission GROUPS. Returns [] on error/empty.
export async function groupsList() {
    try {
        const envelope = await callAct('groupsList', {});
        if (isOk(envelope) && Array.isArray(envelope.data)) return envelope.data;
    } catch (e) { /* act not available */ }
    return [];
}

// Capability rows (permission types × granting groups). Returns [] on error/empty.
export async function permissionsList() {
    try {
        const envelope = await callAct('permissionsList', {});
        if (isOk(envelope) && Array.isArray(envelope.data)) return envelope.data;
    } catch (e) { /* act not available */ }
    return [];
}

// The business's employees + the available groups (for the add/edit selectors).
// Returns { employees:[], groups:[] } — never throws.
export async function employeeList() {
    try {
        const envelope = await callAct('employeeList', {});
        if (isOk(envelope) && envelope.data) {
            const d = envelope.data;
            return {
                employees: Array.isArray(d.employeesList) ? d.employeesList : [],
                groups: Array.isArray(d.groupsList) ? d.groupsList : [],
            };
        }
    } catch (e) { /* act not available */ }
    return { employees: [], groups: [] };
}

// Add an employee — creates a pending SMS-invite for a phone + encrypted groupID.
// Returns { ok, id?, code? }. `employeeGroup` must be the server's encrypted groupID.
export async function addEmployee({ phone, employeeGroup, smsNotification = 1, expiredTime = 24 }) {
    try {
        const envelope = await callAct('addEmployee', {
            phone,
            employeeGroup,
            smsNotification,
            expiredTime,
        });
        if (isOk(envelope)) {
            return { ok: true, id: envelope?.data?.ID };
        }
        return { ok: false, code: Number(envelope?.debug?.statusCode) };
    } catch (e) {
        return { ok: false };
    }
}

export default { groupsList, permissionsList, employeeList, addEmployee };
